/**
 * CampaignAnalyticsDashboard - Comprehensive campaign performance analytics
 * Tracks email campaigns, SMS campaigns, promo codes, conversions, and ROI
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  BarChart3, TrendingUp, TrendingDown, Users, Mail, MessageSquare,
  Target, DollarSign, Percent, Calendar, RefreshCw, Download,
  ArrowUpRight, ArrowDownRight, Eye, MousePointer, ShoppingCart,
  Gift, Zap, Clock, CheckCircle, XCircle, AlertTriangle,
  PieChart, Activity, Send, UserPlus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { api } from '@/utils/apiClient';

export default function CampaignAnalyticsDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('30');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [campaignAnalytics, setCampaignAnalytics] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  const [smsStats, setSmsStats] = useState(null);
  const [promoStats, setPromoStats] = useState(null);
  const [automatedStats, setAutomatedStats] = useState(null);
  const [conversionMetrics, setConversionMetrics] = useState(null);

  // Fetch all analytics data
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const days = parseInt(dateRange);
      const [
        campaignRes,
        emailRes,
        smsRes,
        promoRes,
        automatedRes,
        conversionRes
      ] = await Promise.all([
        api.get('/admin/campaigns/analytics'),
        api.get('/admin/email-automation/stats', { params: { days } }),
        api.get('/admin/sms/delivery-stats', { params: { days } }),
        api.get('/admin/promo-codes/stats'),
        api.get('/automated-campaigns/stats'),
        api.get('/admin/campaigns/conversion-metrics')
      ]);

      setCampaignAnalytics(campaignRes.data);
      setEmailStats(emailRes.data);
      setSmsStats(smsRes.data);
      setPromoStats(promoRes.data);
      setAutomatedStats(automatedRes.data);
      setConversionMetrics(conversionRes.data);
    } catch (err) {
      console.error('Failed to fetch campaign analytics:', err);
      toast.error('Failed to load campaign analytics');
    }
    setLoading(false);
  }, [dateRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const formatCurrency = (value) => `$${(value || 0).toFixed(2)}`;
  const formatPercent = (value) => `${(value || 0).toFixed(1)}%`;
  const formatNumber = (value) => (value || 0).toLocaleString();

  if (loading && !campaignAnalytics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Calculate totals - matching actual API responses
  const totalEmailsSent = emailStats?.summary?.total_emails_sent || 0;
  const totalSMSSent = (smsStats?.delivered || 0) + (smsStats?.sent || 0) + (smsStats?.pending || 0);
  const totalPromoRedemptions = promoStats?.used_codes || 0;
  const totalCampaignSignups = campaignAnalytics?.overview?.total_signups || 0;
  const avgConversionRate = campaignAnalytics?.overview?.email_delivery_rate || 0;

  return (
    <div className="space-y-6" data-testid="campaign-analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Campaign Analytics</h2>
          <p className="text-muted-foreground">Performance metrics across all marketing channels</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[150px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 6 months</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchAnalytics} variant="outline" className="gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <MetricCard
          label="Total Signups"
          value={formatNumber(totalCampaignSignups)}
          icon={UserPlus}
          color="blue"
        />
        <MetricCard
          label="Emails Sent"
          value={formatNumber(totalEmailsSent)}
          icon={Mail}
          color="purple"
        />
        <MetricCard
          label="SMS Sent"
          value={formatNumber(totalSMSSent)}
          icon={MessageSquare}
          color="green"
        />
        <MetricCard
          label="Promo Redemptions"
          value={formatNumber(totalPromoRedemptions)}
          icon={Gift}
          color="orange"
        />
        <MetricCard
          label="Promo Codes"
          value={formatNumber(promoStats?.total_codes || 0)}
          icon={DollarSign}
          color="emerald"
        />
        <MetricCard
          label="Email Delivery"
          value={formatPercent(avgConversionRate)}
          icon={Target}
          color="pink"
        />
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-grid">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="email">Email</TabsTrigger>
          <TabsTrigger value="sms">SMS</TabsTrigger>
          <TabsTrigger value="promos">Promo Codes</TabsTrigger>
          <TabsTrigger value="automated">Automated</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Campaign Performance */}
            {campaignAnalytics?.campaigns && (
              <div className="bg-card rounded-xl p-6 border border-border">
                <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Campaign Performance
                </h3>
                <div className="space-y-4">
                  {campaignAnalytics.campaigns.slice(0, 5).map((campaign) => (
                    <div key={campaign.campaign_id} className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">{campaign.title}</p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <UserPlus className="w-3 h-3" />
                            {campaign.total_signups || 0} signups
                          </span>
                          <span className="flex items-center gap-1">
                            <Gift className="w-3 h-3" />
                            {campaign.referral_signups || 0} referrals
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-foreground">{formatPercent(campaign.email_delivery_rate)}</p>
                        <p className="text-sm text-muted-foreground">email rate</p>
                      </div>
                    </div>
                  ))}
                  {(!campaignAnalytics.campaigns || campaignAnalytics.campaigns.length === 0) && (
                    <p className="text-center text-muted-foreground py-4">No campaign data yet</p>
                  )}
                </div>
              </div>
            )}

            {/* Channel Distribution */}
            <div className="bg-card rounded-xl p-6 border border-border">
              <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
                <PieChart className="w-5 h-5 text-primary" />
                Channel Distribution
              </h3>
              <div className="space-y-4">
                <ChannelBar 
                  label="Email Campaigns" 
                  value={totalEmailsSent} 
                  total={totalEmailsSent + totalSMSSent}
                  color="purple"
                  icon={Mail}
                />
                <ChannelBar 
                  label="SMS Campaigns" 
                  value={totalSMSSent} 
                  total={totalEmailsSent + totalSMSSent}
                  color="green"
                  icon={MessageSquare}
                />
              </div>
              
              <div className="mt-6 pt-4 border-t border-border">
                <h4 className="text-sm font-medium text-muted-foreground mb-3">Delivery Status</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto mb-1" />
                    <p className="text-lg font-bold text-green-600 dark:text-green-400">{smsStats?.delivered || 0}</p>
                    <p className="text-xs text-muted-foreground">Delivered</p>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mx-auto mb-1" />
                    <p className="text-lg font-bold text-yellow-600 dark:text-yellow-400">{smsStats?.pending || 0}</p>
                    <p className="text-xs text-muted-foreground">Pending</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 mx-auto mb-1" />
                    <p className="text-lg font-bold text-red-600 dark:text-red-400">{smsStats?.failed || 0}</p>
                    <p className="text-xs text-muted-foreground">Failed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Conversion Funnel */}
          {conversionMetrics && (
            <div className="bg-card rounded-xl p-6 border border-border">
              <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-primary" />
                Conversion Funnel
              </h3>
              <div className="grid grid-cols-4 gap-4">
                <FunnelStep 
                  label="Page Views" 
                  value={conversionMetrics.total_views || 0}
                  percentage={100}
                  color="blue"
                />
                <FunnelStep 
                  label="Signups" 
                  value={conversionMetrics.total_signups || 0}
                  percentage={conversionMetrics.total_views > 0 
                    ? (conversionMetrics.total_signups / conversionMetrics.total_views * 100) 
                    : 0}
                  color="purple"
                />
                <FunnelStep 
                  label="Code Redeemed" 
                  value={conversionMetrics.total_redemptions || 0}
                  percentage={conversionMetrics.total_signups > 0 
                    ? (conversionMetrics.total_redemptions / conversionMetrics.total_signups * 100) 
                    : 0}
                  color="orange"
                />
                <FunnelStep 
                  label="Orders Placed" 
                  value={conversionMetrics.total_orders || 0}
                  percentage={conversionMetrics.total_redemptions > 0 
                    ? (conversionMetrics.total_orders / conversionMetrics.total_redemptions * 100) 
                    : 0}
                  color="green"
                />
              </div>
            </div>
          )}
        </TabsContent>

        {/* Email Tab */}
        <TabsContent value="email" className="space-y-6">
          {emailStats && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard
                  label="Emails Sent"
                  value={formatNumber(emailStats.summary?.total_emails_sent)}
                  icon={Send}
                  color="blue"
                />
                <MetricCard
                  label="Open Rate"
                  value={formatPercent(emailStats.summary?.open_rate)}
                  icon={Eye}
                  color="green"
                />
                <MetricCard
                  label="Click Rate"
                  value={formatPercent(emailStats.summary?.click_rate)}
                  icon={MousePointer}
                  color="purple"
                />
                <MetricCard
                  label="Active Rules"
                  value={`${emailStats.summary?.active_rules || 0}/${emailStats.summary?.total_rules || 0}`}
                  icon={Zap}
                  color="orange"
                />
              </div>

              {/* Email by Segment */}
              {emailStats.by_segment?.length > 0 && (
                <div className="bg-card rounded-xl p-6 border border-border">
                  <h3 className="font-medium text-foreground mb-4">Performance by Segment</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-2 font-medium text-muted-foreground">Segment</th>
                          <th className="text-right py-3 px-2 font-medium text-muted-foreground">Sent</th>
                          <th className="text-right py-3 px-2 font-medium text-muted-foreground">Opened</th>
                          <th className="text-right py-3 px-2 font-medium text-muted-foreground">Open Rate</th>
                          <th className="text-right py-3 px-2 font-medium text-muted-foreground">Clicked</th>
                          <th className="text-right py-3 px-2 font-medium text-muted-foreground">Click Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailStats.by_segment.map((seg) => (
                          <tr key={seg.segment} className="border-b border-border/50">
                            <td className="py-3 px-2 font-medium text-foreground capitalize">{seg.segment?.replace('_', ' ')}</td>
                            <td className="py-3 px-2 text-right">{seg.emails_sent}</td>
                            <td className="py-3 px-2 text-right">{seg.opened}</td>
                            <td className="py-3 px-2 text-right">
                              <span className={seg.open_rate > 20 ? 'text-green-600 dark:text-green-400' : ''}>
                                {formatPercent(seg.open_rate)}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-right">{seg.clicked}</td>
                            <td className="py-3 px-2 text-right">
                              <span className={seg.click_rate > 5 ? 'text-green-600 dark:text-green-400' : ''}>
                                {formatPercent(seg.click_rate)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Discount Stats */}
              {emailStats.discounts && (
                <div className="bg-card rounded-xl p-6 border border-border">
                  <h3 className="font-medium text-foreground mb-4">Email Discount Performance</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-muted/30 rounded-lg">
                      <p className="text-2xl font-bold text-foreground">{emailStats.discounts.generated || 0}</p>
                      <p className="text-sm text-muted-foreground">Codes Generated</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">{emailStats.discounts.redeemed || 0}</p>
                      <p className="text-sm text-muted-foreground">Codes Redeemed</p>
                    </div>
                    <div className="text-center p-4 bg-muted/30 rounded-lg">
                      <p className="text-2xl font-bold text-foreground">{formatCurrency(emailStats.discounts.total_discount_value || 0)}</p>
                      <p className="text-sm text-muted-foreground">Total Discount Value</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* SMS Tab */}
        <TabsContent value="sms" className="space-y-6">
          {smsStats && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <MetricCard
                  label="Delivered"
                  value={formatNumber(smsStats.delivered)}
                  icon={CheckCircle}
                  color="green"
                />
                <MetricCard
                  label="Sent"
                  value={formatNumber(smsStats.sent)}
                  icon={Send}
                  color="blue"
                />
                <MetricCard
                  label="Queued"
                  value={formatNumber(smsStats.queued)}
                  icon={Clock}
                  color="yellow"
                />
                <MetricCard
                  label="Pending"
                  value={formatNumber(smsStats.pending)}
                  icon={AlertTriangle}
                  color="orange"
                />
                <MetricCard
                  label="Failed"
                  value={formatNumber(smsStats.failed)}
                  icon={XCircle}
                  color="red"
                />
              </div>

              <div className="bg-card rounded-xl p-6 border border-border">
                <h3 className="font-medium text-foreground mb-4">SMS Delivery Rate</h3>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="h-4 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 rounded-full transition-all"
                        style={{ 
                          width: `${totalSMSSent > 0 ? (smsStats.delivered / totalSMSSent * 100) : 0}%` 
                        }}
                      />
                    </div>
                  </div>
                  <span className="text-lg font-bold text-green-600 dark:text-green-400">
                    {totalSMSSent > 0 ? formatPercent(smsStats.delivered / totalSMSSent * 100) : '0%'}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {smsStats.delivered} of {totalSMSSent} messages successfully delivered
                </p>
              </div>
            </>
          )}
        </TabsContent>

        {/* Promo Codes Tab */}
        <TabsContent value="promos" className="space-y-6">
          {promoStats && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard
                  label="Total Codes"
                  value={formatNumber(promoStats.total_codes)}
                  icon={Gift}
                  color="blue"
                />
                <MetricCard
                  label="Used Codes"
                  value={formatNumber(promoStats.used_codes)}
                  icon={CheckCircle}
                  color="green"
                />
                <MetricCard
                  label="Available Codes"
                  value={formatNumber(promoStats.available_codes)}
                  icon={Percent}
                  color="purple"
                />
                <MetricCard
                  label="Pool Capacity"
                  value={formatPercent(promoStats.pool_percentage || 0)}
                  icon={DollarSign}
                  color="orange"
                />
              </div>

              {/* Discount Breakdown */}
              {promoStats.discount_breakdown && (
                <div className="bg-card rounded-xl p-6 border border-border">
                  <h3 className="font-medium text-foreground mb-4">Discount Breakdown</h3>
                  <div className="space-y-3">
                    {Object.entries(promoStats.discount_breakdown).map(([percent, count]) => (
                      <div key={percent} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div>
                          <p className="font-medium text-foreground">{percent}% Discount Codes</p>
                          <p className="text-sm text-muted-foreground">{formatNumber(count)} codes</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-green-600 dark:text-green-400">
                            {promoStats.total_codes > 0 
                              ? formatPercent(count / promoStats.total_codes * 100)
                              : '0%'}
                          </p>
                          <p className="text-sm text-muted-foreground">of total</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* Automated Campaigns Tab */}
        <TabsContent value="automated" className="space-y-6">
          {automatedStats && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard
                  label="Upcoming Birthdays"
                  value={formatNumber(automatedStats.birthday?.upcoming_birthdays)}
                  icon={Gift}
                  color="pink"
                />
                <MetricCard
                  label="Birthday Sent"
                  value={formatNumber(automatedStats.birthday?.total_sent)}
                  icon={Send}
                  color="purple"
                />
                <MetricCard
                  label="Eligible for Win-Back"
                  value={formatNumber(automatedStats.reengagement?.eligible_contacts)}
                  icon={Users}
                  color="orange"
                />
                <MetricCard
                  label="Win-Back Sent"
                  value={formatNumber(automatedStats.reengagement?.total_sent)}
                  icon={Mail}
                  color="blue"
                />
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                {/* Birthday Stats */}
                <div className="bg-card rounded-xl p-6 border border-border">
                  <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
                    <Gift className="w-5 h-5 text-pink-500" />
                    Birthday Campaign
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                      <span className="text-muted-foreground">Status</span>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        automatedStats.birthday?.enabled 
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                      }`}>
                        {automatedStats.birthday?.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                      <span className="text-muted-foreground">Discount</span>
                      <span className="font-medium">{automatedStats.birthday?.discount_percent || 0}%</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                      <span className="text-muted-foreground">Conversion Rate</span>
                      <span className="font-medium text-green-600 dark:text-green-400">
                        {formatPercent(automatedStats.birthday?.conversion_rate)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Re-engagement Stats */}
                <div className="bg-card rounded-xl p-6 border border-border">
                  <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-orange-500" />
                    Re-engagement Campaign
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                      <span className="text-muted-foreground">Status</span>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        automatedStats.reengagement?.enabled 
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                      }`}>
                        {automatedStats.reengagement?.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                      <span className="text-muted-foreground">Inactive Threshold</span>
                      <span className="font-medium">{automatedStats.reengagement?.inactive_days || 60} days</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                      <span className="text-muted-foreground">Recovery Rate</span>
                      <span className="font-medium text-green-600 dark:text-green-400">
                        {formatPercent(automatedStats.reengagement?.recovery_rate)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Metric Card Component
function MetricCard({ label, value, icon: Icon, color }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    green: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
    orange: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
    pink: 'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400',
    emerald: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
    yellow: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
    red: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
  };

  return (
    <div className="bg-card rounded-xl p-4 border border-border">
      <div className="flex items-center justify-between mb-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="text-xl font-bold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

// Channel Bar Component
function ChannelBar({ label, value, total, color, icon: Icon }) {
  const percentage = total > 0 ? (value / total * 100) : 0;
  const colorClasses = {
    purple: 'bg-purple-500',
    green: 'bg-green-500',
    blue: 'bg-blue-500'
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">{label}</span>
        </div>
        <span className="text-sm text-muted-foreground">{value.toLocaleString()} ({percentage.toFixed(1)}%)</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full ${colorClasses[color]}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

// Funnel Step Component
function FunnelStep({ label, value, percentage, color }) {
  const colorClasses = {
    blue: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20',
    purple: 'border-purple-500 bg-purple-50 dark:bg-purple-900/20',
    orange: 'border-orange-500 bg-orange-50 dark:bg-orange-900/20',
    green: 'border-green-500 bg-green-50 dark:bg-green-900/20'
  };

  return (
    <div className={`rounded-lg p-4 border-l-4 ${colorClasses[color]}`}>
      <p className="text-2xl font-bold text-foreground">{value.toLocaleString()}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-xs text-muted-foreground mt-1">{percentage.toFixed(1)}%</p>
    </div>
  );
}
